/**
 * TEMPORARY FALLBACK: CSS Anchor Positioning polyfill via JS.
 *
 * This hook repositions a popover element relative to its anchor chip using
 * getBoundingClientRect, for browsers that don't support CSS anchor-name
 * (Safari < 26, Firefox < 147, Chrome < 125).
 *
 * Positioning logic:
 * - Default: popover is centered horizontally beneath the chip
 * - Left edge: if centering would overflow the left viewport edge, align left edges
 * - Right edge: if centering would overflow the right viewport edge, align right edges
 *
 * REMOVAL: Once Datadog RUM shows negligible traffic from unsupported browsers,
 * delete this file and remove the useAnchorPositionFallback call from useFilterPopover.ts.
 * The CSS in FilterChip.module.scss (anchor(), position-anchor) handles positioning natively.
 *
 */
import { useCallback, useEffect } from 'react';

const POPOVER_OFFSET_PX = 0;

const supportsAnchorPositioning = (): boolean => typeof CSS !== 'undefined' && CSS.supports('anchor-name', '--x');

interface UseAnchorPositionFallbackOptions {
	chipWrapperRef: React.RefObject<HTMLDivElement>;
	isOpen: boolean;
	popoverRef: React.RefObject<HTMLDivElement>;
}

export const useAnchorPositionFallback = ({
	chipWrapperRef,
	isOpen,
	popoverRef,
}: UseAnchorPositionFallbackOptions): void => {
	const needsFallback = !supportsAnchorPositioning();

	const applyPosition = useCallback(() => {
		if (!needsFallback) return;

		const chip = chipWrapperRef.current;
		const popover = popoverRef.current;
		if (!chip || !popover) return;

		const chipRect = chip.getBoundingClientRect();
		const popoverWidth = popover.offsetWidth;
		const viewportWidth = document.documentElement.clientWidth;

		// Center the popover beneath the chip
		const chipCenter = chipRect.left + chipRect.width / 2;
		let left = chipCenter - popoverWidth / 2;

		// Clamp: if overflowing left, align left edges
		if (left < 0) {
			left = chipRect.left;
		}

		// Clamp: if overflowing right, align right edges
		if (left + popoverWidth > viewportWidth) {
			left = chipRect.right - popoverWidth;
		}

		popover.style.top = `${chipRect.bottom + POPOVER_OFFSET_PX}px`;
		popover.style.left = `${left}px`;
		popover.style.opacity = '1';
	}, [chipWrapperRef, needsFallback, popoverRef]);

	// Hide popover until positioned to prevent flicker
	useEffect(() => {
		if (!needsFallback) return;
		const popover = popoverRef.current;
		if (!popover) return;
		popover.style.opacity = '0';
	}, [needsFallback, popoverRef]);

	// Position on open
	useEffect(() => {
		if (isOpen) applyPosition();
	}, [isOpen, applyPosition]);

	// Continuously reposition on scroll/resize while open
	useEffect(() => {
		if (!needsFallback || !isOpen) return undefined;

		let rafId: number;
		const reposition = () => {
			cancelAnimationFrame(rafId);
			rafId = requestAnimationFrame(applyPosition);
		};

		window.addEventListener('scroll', reposition, { capture: true, passive: true });
		window.addEventListener('resize', reposition, { passive: true });

		return () => {
			cancelAnimationFrame(rafId);
			window.removeEventListener('scroll', reposition, { capture: true });
			window.removeEventListener('resize', reposition);
		};
	}, [needsFallback, isOpen, applyPosition]);
};
